import clsx from "clsx";
import { useEffect, useId, useRef, useState } from "react";
import { Button, Col } from "@/components";
import "./GroupCodeExemptionFilterDropDown.scss";
import { Close } from "@/icons";

export type FinancialClassCode = {
  code: string;
  codingScheme: string;
  labels: {
    it: string;
    en?: string;
  };
};

type GroupCodeExemptionDropDownProps = {
  size?: "small" | "large";
  items: FinancialClassCode[];
  className?: string;
  selectedGroupCode: FinancialClassCode | null;
  setSelectedGroupCode: (groupCode: FinancialClassCode | null) => void;
  placeholder?: string;
  btnText?: string;
  onSearch?: (groupCode: FinancialClassCode | null) => void;
};

export const GroupCodeExemptionFilterDropDown = ({
  size = "large",
  items = [],
  className,
  selectedGroupCode,
  setSelectedGroupCode,
  placeholder = "",
  btnText = "Cerca",
  onSearch,
}: GroupCodeExemptionDropDownProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSelectedCode, setTempSelectedCode] =
    useState<FinancialClassCode | null>(selectedGroupCode);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const labelText = placeholder || "Codice esenzione";

  const filteredItems = items.filter((groupCode) => {
    return groupCode.labels.it.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    setTempSelectedCode(selectedGroupCode);
  }, [selectedGroupCode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded) {
      setSearchTerm("");
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  const displayValue =
    !isExpanded && tempSelectedCode ? tempSelectedCode.labels.it : searchTerm;

  const showCloseButton = tempSelectedCode !== null || searchTerm.length > 0;

  const highlightMatch = (text: string, query: string) => {
    const baseStyle = { color: "black", fontSize: "0.85rem" };

    if (!query || query.trim() === "") {
      return <span style={baseStyle}>{text}</span>;
    }

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return <span style={baseStyle}>{text}</span>;

    const beforeMatch = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const afterMatch = text.slice(index + query.length);

    return (
      <span style={baseStyle}>
        {beforeMatch}
        <span style={{ fontSize: "0.85rem" }} className="text-primary fw-bold">
          {match}
        </span>
        {afterMatch}
      </span>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className={clsx(
        "ui-related-dropdown-group-code",
        "br-x4 bg-border-secondary w-full",
        className,
      )}
      style={size === "large" ? { maxWidth: "100%" } : { maxWidth: "553px" }}
    >
      <div className="ui-input-container-group-code">
        <label className="visually-hidden" htmlFor={inputId}>
          {labelText}
        </label>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className={clsx(
            "ui-related-dropdown-input-group-code",
            "bg-white fs-6 br-x4 w-full pl-x24",
            { active: isExpanded },
          )}
          value={displayValue}
          onChange={(event) => {
            if (isExpanded) {
              setSearchTerm(event.target.value);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isExpanded}
          aria-controls={listboxId}
        />

        {showCloseButton && (
          <button
            type="button"
            className="ui-close-button-group-code"
            onClick={(event) => {
              event.stopPropagation();
              setTempSelectedCode(null);
              setSearchTerm("");
            }}
            aria-label="Cancella filtro del codice esenzione"
          >
            <span aria-hidden="true">
              <Close width={20} />
            </span>
          </button>
        )}

        <div className="ui-div-btn-group-code">
          <Button
            className={clsx(
              "ui-btn-search-group-code",
              "bg-primary text-white fs-7 fw-semibold br-x4",
            )}
            onClick={() => {
              setSelectedGroupCode(tempSelectedCode);
              onSearch?.(tempSelectedCode);
            }}
          >
            {btnText}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="ui-related-dropdown-options-group-code">
          <div className="px-x24 shadow w-full">
            <Col
              gap={"x4"}
              text={"primary"}
              fs={"custom"}
              fw={"regular"}
              className={"ui-options-list-group-code fw-regular"}
              role="listbox"
              aria-label="Risultati ricerca codice esenzione"
              id={listboxId}
            >
              {filteredItems.length > 0 ? (
                filteredItems.map((groupCode, index) => {
                  const isSelected =
                    tempSelectedCode?.code === groupCode.code;

                  return (
                    <div
                      className="ui-option-item-group-code"
                      key={groupCode.code || index}
                      role="option"
                      tabIndex={0}
                      aria-selected={isSelected}
                      onClick={() => {
                        setTempSelectedCode(groupCode);
                        setIsExpanded(false);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setTempSelectedCode(groupCode);
                          setIsExpanded(false);
                        }
                      }}
                    >
                      {highlightMatch(groupCode.labels.it, searchTerm)}
                    </div>
                  );
                })
              ) : (
                <div
                  className="ui-option-item-group-code no-results-group-code"
                  role="status"
                  aria-live="polite"
                >
                  <span style={{ color: "gray" }}>
                    Nessun risultato trovato
                  </span>
                </div>
              )}
            </Col>
          </div>
        </div>
      )}
    </div>
  );
};

